import React, { useState } from 'react';
import { Info, X } from 'lucide-react';
import { Label } from "../components/ui/Label";
import { cn } from "../lib/utils";
import FormattedHelpText from './FormattedHelpText';

interface FormFieldProps {
  label: string | React.ReactNode;
  required?: boolean;
  helpText?: string;
  tooltipContent?: string;
  longHelpText?: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
  helpTitle?: string;
}

export const FormField = ({
  label,
  required = false,
  helpText,
  tooltipContent,
  longHelpText,
  error,
  children,
  className,
  helpTitle
}: FormFieldProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // Use the most detailed help text available
  const displayHelpText = longHelpText || helpText || tooltipContent;

  return (
    <div className={cn("relative", className)}>
      {/* Main Form Field */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {displayHelpText && (
            <button
              type="button"
              onClick={() => setIsDrawerOpen(true)}
              className="inline-flex items-center justify-center text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              <Info className="h-4 w-4" />
            </button>
          )}
        </div>
        {children}
        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}
      </div>

      {/* Help Drawer */}
      {displayHelpText && (
        <div
          className={cn(
            "fixed inset-y-0 right-0 w-80 bg-white shadow-lg border-l border-gray-200 transform transition-transform duration-200 ease-in-out z-50",
            isDrawerOpen ? "translate-x-0" : "translate-x-full"
          )}
        >
          <div className="h-full flex flex-col">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h4 className="font-medium text-lg">
                {helpTitle || `${label} Help`}
              </h4>
              <button
                type="button"
                onClick={() => setIsDrawerOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="prose prose-sm max-w-none">
                <FormattedHelpText text={displayHelpText} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}
    </div>
  );
};